import { annualSavingsPercent, type BillingCycle } from "@/lib/tiers";

/**
 * Monthly / yearly billing switch shared by the pricing page and the paywall.
 * Rendered as a radio group so keyboard and screen reader users can switch
 * billing views the same way as pointer users.
 */
export function BillingCycleToggle({
  value,
  onChange,
  className = "",
}: {
  value: BillingCycle;
  onChange: (cycle: BillingCycle) => void;
  className?: string;
}) {
  // Advertise the smaller of the two discounts so the badge is true for both tiers.
  const minSaving = Math.min(
    annualSavingsPercent("standard"),
    annualSavingsPercent("pro"),
  );

  const options: { id: BillingCycle; label: string }[] = [
    { id: "monthly", label: "Monthly" },
    { id: "yearly", label: "Yearly" },
  ];

  return (
    <div
      role="radiogroup"
      aria-label="Billing period"
      className={`inline-flex items-center gap-1 rounded-full border border-border bg-card p-1 ${className}`}
    >
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.id)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
              active
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {opt.label}
            {opt.id === "yearly" && minSaving > 0 && (
              <span
                className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                  active ? "bg-primary-foreground/20" : "bg-primary/15 text-primary"
                }`}
              >
                Save {minSaving}%+
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
